import { HOST_SEAT_ID, type Game } from '../game/Game';
import { hashBrownDoneness } from '../game/kitchen';
import { EGG_LABEL, TOPPING_INFO, WAFFLE_LABEL, isAllTheWay, orderedKinds } from '../game/lingo';
import { TOPPINGS, type ItemKind, type Ticket, type Topping } from '../game/types';
import { STOOL_COUNT } from '../render/layout';
import { ticketColor } from '../render/theme';
import { h, kids, setAttr, setText, toggleClass } from './dom';
import { ICONS, iconImg } from './iconImages';

interface Card {
  el: HTMLButtonElement;
  fill: HTMLElement;
  sig: string;
}

export function seatLabel(seatId: number): string {
  if (seatId === HOST_SEAT_ID) return '???';
  return seatId < STOOL_COUNT ? `STOOL ${seatId + 1}` : `BOOTH ${seatId - STOOL_COUNT + 1}`;
}

function itemState(game: Game, t: Ticket, kind: ItemKind): 'done' | 'cooking' | 'todo' {
  if (t.plated[kind]) return 'done';
  return game.isCooking(t, kind) ? 'cooking' : 'todo';
}

const TAG_LABEL: Partial<Record<Ticket['tags'][number], string>> = {
  proposal: '💍 PROPOSAL',
  host: '👁 ???',
  usual: '⭐ REGULAR',
  eggKaren: '🥚 +50¢',
  calmed: '💛 CALMED · TIPS BIG',
};

/** The ticket rail (every open order) and the call bar (the active order, in full lingo). */
export class TicketRail {
  readonly el: HTMLElement;
  readonly callbar: HTMLElement;
  private readonly callNo: HTMLElement;
  private readonly callText: HTMLElement;
  private readonly callHint: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly cards = new Map<number, Card>();
  private callSig = '';
  hint = '';
  onSelect: (ticketId: number) => void = () => {};

  constructor() {
    this.empty = h(
      'div',
      { class: 'rail__empty' },
      'No tickets… enjoy the silence. It won’t last.',
    );
    this.el = h('section', { class: 'rail', 'aria-label': 'Order tickets' }, this.empty);
    this.callNo = h('span', { class: 'callbar__no' });
    this.callText = h('span', { class: 'callbar__text' });
    this.callHint = h('span', { class: 'callbar__hint' });
    this.callbar = h(
      'section',
      { class: 'callbar', 'aria-live': 'polite' },
      this.callNo,
      this.callText,
      this.callHint,
    );
  }

  reset(): void {
    for (const c of this.cards.values()) c.el.remove();
    this.cards.clear();
    this.callSig = '';
  }

  update(game: Game): void {
    const open = game.openTickets();
    const seen = new Set<number>();
    open.forEach((t, position) => {
      seen.add(t.id);
      let card = this.cards.get(t.id);
      if (!card) {
        card = this.createCard(t);
        this.cards.set(t.id, card);
      }
      if (card.el.parentElement !== this.el || this.el.children[position + 1] !== card.el) {
        this.el.insertBefore(card.el, this.el.children[position + 1] ?? null);
      }
      const sig = this.signature(game, t, position);
      if (sig !== card.sig) {
        card.sig = sig;
        this.fillCard(game, card, t, position);
      }
      const customer = game.customer(t.customerId);
      const patience = customer?.patience ?? 1;
      card.fill.style.transform = `scaleX(${patience})`;
      card.fill.dataset.level = patience > 0.55 ? 'ok' : patience > 0.25 ? 'warn' : 'bad';
      toggleClass(card.el, 'is-frozen', game.patienceFrozen);
      toggleClass(card.el, 'is-urgent', patience < 0.25 && !game.patienceFrozen);
    });
    for (const [id, card] of this.cards) {
      if (!seen.has(id)) {
        card.el.classList.add('is-leaving');
        setTimeout(() => card.el.remove(), 250);
        this.cards.delete(id);
      }
    }
    toggleClass(this.empty, 'is-hidden', open.length > 0);
    this.updateCallbar(game);
  }

  private createCard(t: Ticket): Card {
    const fill = h('i', { class: 'ticket__fill' });
    const el = h('button', { class: 'ticket', type: 'button' });
    el.style.setProperty('--tc', ticketColor(t.color));
    el.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      this.onSelect(t.id);
    });
    el.addEventListener('click', (e) => {
      if (e.detail === 0) this.onSelect(t.id);
    });
    const card = { el, fill, sig: '' };
    el.classList.add('is-entering');
    setTimeout(() => el.classList.remove('is-entering'), 300);
    return card;
  }

  private signature(game: Game, t: Ticket, position: number): string {
    const slot = game.slotFor(t.id);
    return [
      position,
      game.activeTicketId === t.id,
      game.isRevealed(t),
      orderedKinds(t.order)
        .map((k) => itemState(game, t, k))
        .join(','),
      slot ? slot.toppings.join('.') : '',
    ].join('|');
  }

  private fillCard(game: Game, card: Card, t: Ticket, position: number): void {
    const active = game.activeTicketId === t.id;
    toggleClass(card.el, 'is-active', active);
    setAttr(card.el, 'aria-pressed', active ? 'true' : 'false');
    toggleClass(card.el, 'is-host', t.tags.includes('host'));
    toggleClass(card.el, 'is-proposal', t.tags.includes('proposal'));
    const revealed = game.isRevealed(t);
    const items: HTMLElement[] = [];
    if (!revealed) {
      items.push(h('li', { class: 'ti ti--usual' }, '“THE USUAL.”'));
    } else {
      const o = t.order;
      if (o.hashBrowns) {
        const slot = game.slotFor(t.id);
        const added = new Set<Topping>(slot?.toppings ?? []);
        const minis = isAllTheWay(o.hashBrowns.toppings)
          ? [h('span', { class: 'ti__atw' }, 'ALL THE WAY')]
          : o.hashBrowns.toppings.map((tp) => {
              const img = iconImg(ICONS.topping(tp), 'ti__mini', TOPPING_INFO[tp].lingo);
              if (added.has(tp)) img.classList.add('is-added');
              return img;
            });
        items.push(
          h(
            'li',
            { class: `ti is-${itemState(game, t, 'hashBrowns')}` },
            iconImg(ICONS.hashBrowns(), 'ti__icon', 'Hash browns'),
            h(
              'span',
              { class: 'ti__minis' },
              ...(minis.length ? minis : [h('span', { class: 'ti__plain' }, 'PLAIN')]),
            ),
            o.hashBrowns.well ? h('span', { class: 'ti__well' }, 'WELL') : null,
            h(
              'span',
              { class: 'ti__words' },
              isAllTheWay(o.hashBrowns.toppings)
                ? 'ALL THE WAY'
                : o.hashBrowns.toppings.map((tp) => TOPPING_INFO[tp].lingo).join(' · ') ||
                    'JUST SCATTERED',
            ),
          ),
        );
      }
      if (o.waffle) {
        items.push(
          h(
            'li',
            { class: `ti is-${itemState(game, t, 'waffle')}` },
            iconImg(ICONS.waffle(o.waffle), 'ti__icon', WAFFLE_LABEL[o.waffle]),
            h('span', { class: 'ti__text' }, o.waffle === 'pecan' ? 'PECAN' : 'WAFFLE'),
          ),
        );
      }
      if (o.eggs) {
        items.push(
          h(
            'li',
            { class: `ti is-${itemState(game, t, 'eggs')}` },
            iconImg(ICONS.eggs(o.eggs), 'ti__icon', EGG_LABEL[o.eggs]),
            h('span', { class: 'ti__text' }, o.eggs === 'scrambled' ? 'SCRAM' : 'OVR EZ'),
          ),
        );
      }
      if (o.bacon) {
        items.push(
          h(
            'li',
            { class: `ti is-${itemState(game, t, 'bacon')}` },
            iconImg(ICONS.bacon(), 'ti__icon', 'Bacon'),
            h('span', { class: 'ti__text' }, 'BACON'),
          ),
        );
      }
    }
    const tag = t.tags.map((x) => TAG_LABEL[x]).find(Boolean);
    card.el.replaceChildren(
      ...kids(
        h('span', { class: 'ticket__key', 'aria-hidden': 'true' }, String(position + 1)),
        h(
          'div',
          { class: 'ticket__head' },
          h('span', { class: 'ticket__no' }, `#${t.no}`),
          h('span', { class: 'ticket__seat' }, seatLabel(t.seatId)),
        ),
        tag ? h('div', { class: 'ticket__tag' }, tag) : null,
        h('ul', { class: 'ticket__items' }, ...items),
        h('div', { class: 'ticket__patience' }, card.fill),
      ),
    );
    const label = revealed
      ? `Ticket ${t.no}, ${seatLabel(t.seatId)}: ${orderedKinds(t.order)
          .map((k) => `${k} ${itemState(game, t, k)}`)
          .join(', ')}`
      : `Ticket ${t.no}: the usual`;
    setAttr(card.el, 'aria-label', label);
  }

  private updateCallbar(game: Game): void {
    const t = game.activeTicket();
    const slot = t ? game.slotFor(t.id) : undefined;
    const doneness = slot ? hashBrownDoneness(slot.time, game.cook) : null;
    const sig = t
      ? [
          t.id,
          game.isRevealed(t),
          orderedKinds(t.order)
            .map((k) => itemState(game, t, k))
            .join(','),
          slot?.toppings.join('.') ?? '',
          doneness,
          this.hint,
        ].join('|')
      : `none|${this.hint}`;
    if (sig === this.callSig) return;
    this.callSig = sig;
    if (!t) {
      this.callbar.style.removeProperty('--tc');
      setText(this.callNo, '—');
      this.callText.replaceChildren(
        h('span', { class: 'part is-muted' }, 'Waiting on the next ticket…'),
      );
      setText(this.callHint, this.hint);
      return;
    }
    this.callbar.style.setProperty('--tc', ticketColor(t.color));
    setText(this.callNo, `#${t.no}`);
    if (!game.isRevealed(t)) {
      this.callText.replaceChildren(
        h('span', { class: 'part' }, '“THE USUAL.” — he’ll tell you in a sec…'),
      );
      setText(this.callHint, this.hint);
      return;
    }
    const parts: HTMLElement[] = [];
    const o = t.order;
    const sep = (): HTMLElement => h('span', { class: 'sep' }, ' · ');
    if (o.hashBrowns) {
      const state = itemState(game, t, 'hashBrowns');
      const added = new Set<Topping>(slot?.toppings ?? []);
      const words: HTMLElement[] = [
        h(
          'span',
          { class: `word ${state === 'done' || slot ? 'is-added' : ''}` },
          o.hashBrowns.well ? 'SCATTERED WELL' : 'SCATTERED',
        ),
      ];
      if (isAllTheWay(o.hashBrowns.toppings)) {
        const have = TOPPINGS.filter((x) => added.has(x)).length;
        words.push(
          h('span', { class: `word ${have === 8 ? 'is-added' : ''}` }, ` ALL THE WAY (${have}/8)`),
        );
      } else {
        for (const tp of o.hashBrowns.toppings) {
          words.push(
            h(
              'span',
              { class: `word ${added.has(tp) || state === 'done' ? 'is-added' : ''}` },
              ` ${TOPPING_INFO[tp].lingo}`,
            ),
          );
        }
      }
      const extras = (slot?.toppings ?? []).filter((x) => !o.hashBrowns?.toppings.includes(x));
      for (const x of extras)
        words.push(
          h('span', { class: 'word is-wrong' }, ` +${TOPPING_INFO[x].ingredient.toUpperCase()}?`),
        );
      if (doneness && state === 'cooking')
        words.push(
          h('span', { class: `doneness doneness--${doneness}` }, ` [${doneness.toUpperCase()}]`),
        );
      parts.push(h('span', { class: `part is-${state}` }, ...words));
    }
    if (o.waffle) {
      if (parts.length) parts.push(sep());
      parts.push(
        h('span', { class: `part is-${itemState(game, t, 'waffle')}` }, WAFFLE_LABEL[o.waffle]),
      );
    }
    if (o.eggs) {
      if (parts.length) parts.push(sep());
      parts.push(h('span', { class: `part is-${itemState(game, t, 'eggs')}` }, EGG_LABEL[o.eggs]));
    }
    if (o.bacon) {
      if (parts.length) parts.push(sep());
      parts.push(h('span', { class: `part is-${itemState(game, t, 'bacon')}` }, 'BACON'));
    }
    this.callText.replaceChildren(...parts);
    setText(this.callHint, this.hint);
  }
}
